import { useCallback, useEffect, useRef, useState } from "react"

// 音源の試聴。共有の `Audio` インスタンスを 1 つだけ持ち回り、
// 「常に 1 曲だけ再生」をこの hook の構造で保証する（決定 #6）。
//
// ブラウザ標準の `<audio controls>` は使わず、再生/停止のアイコンボタンだけの簡易実装にする。
// モーダルの ▶ とカードの ▶ の両方に同じ制御を配るため、AudioSection で 1 回だけ呼ぶこと。
export const useAudioPlayer = () => {
  const elementRef = useRef<HTMLAudioElement | null>(null)
  const [playingId, setPlayingId] = useState<string | null>(null)

  const stop = useCallback(() => {
    const el = elementRef.current
    if (el) {
      el.pause()
      // src を空にしないと停止後もダウンロードが続くブラウザがある
      el.removeAttribute("src")
      elementRef.current = null
    }
    setPlayingId(null)
  }, [])

  // 同じ音源の再生中なら停止、それ以外は前の曲を止めてから再生する。
  // preview_url が null の音源は再生できない（呼び出し側で「試聴不可」を表示する）。
  const toggle = useCallback((audioId: string, previewUrl: string | null) => {
    if (!previewUrl) return

    if (playingId === audioId) {
      stop()
      return
    }

    stop()
    const el = new window.Audio(previewUrl)
    el.addEventListener("ended", () => {
      elementRef.current = null
      setPlayingId(null)
    })
    elementRef.current = el
    setPlayingId(audioId)

    // jsdom では play() が undefined を返すため、Promise かどうかを確かめてから catch する
    const played = el.play() as Promise<void> | undefined
    if (played && typeof played.catch === "function") {
      played.catch(() => {
        // 別音源へ切り替えた直後は前の音源の play() が AbortError で reject する。
        // その reject で今鳴っている音源の playingId を消さないよう、自分が現行要素の
        // ときだけ状態を戻す（決定 #6「常に 1 曲だけ再生」の維持）
        if (elementRef.current !== el) return
        elementRef.current = null
        setPlayingId(null)
      })
    }
  }, [playingId, stop])

  // アンマウント時（モーダルを閉じてフォームを離れる場合も含む）に必ず停止する
  useEffect(() => () => { stop() }, [stop])

  return { playingId, toggle, stop }
}

export type AudioPlayerState = ReturnType<typeof useAudioPlayer>
